import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionLimits } from '../hooks/useSubscription';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, DollarSign, Trash2, TrendingUp, Award, Leaf } from 'lucide-react';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { EMOJIS } from '../constants/emojis';

interface AnalyticsData {
  totalItemsAdded: number;
  itemsUsed: number;
  itemsWasted: number;
  moneySaved: number;
  moneyWasted: number;
  wasteRate: number;
  co2Prevented: number;
  mealsPreserved: number;
}

export function Analytics() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const subscription = useSubscriptionLimits();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalItemsAdded: 0,
    itemsUsed: 0,
    itemsWasted: 0,
    moneySaved: 0,
    moneyWasted: 0,
    wasteRate: 0,
    co2Prevented: 0,
    mealsPreserved: 0,
  });

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    loadAnalytics();
  }, [user, navigate]);

  // Redirect premium users to advanced analytics
  useEffect(() => {
    if (!loading && subscription.hasAdvancedAnalytics) {
      navigate('/advanced-analytics', { replace: true });
    }
  }, [loading, subscription.hasAdvancedAnalytics, navigate]);

  async function loadAnalytics() {
    try {
      setLoading(true);

      // Get user's household
      const householdsQuery = query(
        collection(db, 'households'),
        where('members', 'array-contains', user!.uid),
        limit(1)
      );

      const householdsSnapshot = await getDocs(householdsQuery);

      if (householdsSnapshot.empty) {
        setLoading(false);
        return;
      }

      const householdId = householdsSnapshot.docs[0].id;

      // Get all items for the household
      const itemsQuery = query(
        collection(db, 'items'),
        where('householdId', '==', householdId)
      );

      const itemsSnapshot = await getDocs(itemsQuery);
      const items = itemsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Calculate analytics
      const totalItemsAdded = items.length;
      const itemsUsed = items.filter((item: any) => item.status === 'used').length;
      const itemsWasted = items.filter((item: any) => item.status === 'wasted').length;

      const moneySaved = items
        .filter((item: any) => item.status === 'used')
        .reduce((total: number, item: any) => total + (item.price || 0), 0);

      const moneyWasted = items
        .filter((item: any) => item.status === 'wasted')
        .reduce((total: number, item: any) => total + (item.price || 0), 0);

      const wasteRate =
        itemsUsed + itemsWasted > 0
          ? (itemsWasted / (itemsUsed + itemsWasted)) * 100
          : 0;

      // Environmental calculations
      const co2Prevented = itemsUsed * 1.5; // kg of CO2
      const mealsPreserved = itemsUsed;

      setAnalytics({
        totalItemsAdded,
        itemsUsed,
        itemsWasted,
        moneySaved,
        moneyWasted,
        wasteRate,
        co2Prevented,
        mealsPreserved,
      });

      setLoading(false);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
              <div className="space-y-4">
                <div className="h-24 bg-gray-200 rounded"></div>
                <div className="h-24 bg-gray-200 rounded"></div>
                <div className="h-24 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-mint/30 via-neutral-bg to-primary-mint/20 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-600 hover:text-primary-green transition-colors mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to Dashboard</span>
        </button>

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-gray-100">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Basic Analytics</h1>
          <p className="text-gray-600">Track your food waste and savings</p>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Money Saved */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div className="text-4xl font-bold text-gray-900 mb-2">
              ${analytics.moneySaved.toFixed(2)}
            </div>
            <div className="text-sm font-semibold text-gray-700 mb-1">Money Saved</div>
            <div className="text-xs text-gray-600">{analytics.itemsUsed} items used</div>
          </div>

          {/* Money Wasted */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <TrendingUp className="w-6 h-6 text-red-600" />
            </div>
            <div className="text-4xl font-bold text-gray-900 mb-2">
              ${analytics.moneyWasted.toFixed(2)}
            </div>
            <div className="text-sm font-semibold text-gray-700 mb-1">Money Wasted</div>
            <div className="text-xs text-gray-600">{analytics.itemsWasted} items wasted</div>
          </div>

          {/* Success Rate */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-primary-green">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-primary-mint rounded-full flex items-center justify-center">
                <Award className="w-6 h-6 text-primary-green" />
              </div>
              <span className="text-2xl">{EMOJIS.CHECKMARK}</span>
            </div>
            <div className="text-4xl font-bold text-gray-900 mb-2">
              {Math.round(100 - analytics.wasteRate)}%
            </div>
            <div className="text-sm font-semibold text-gray-700 mb-1">Success Rate</div>
            <div className="text-xs text-gray-600">{analytics.totalItemsAdded} total items</div>
          </div>

          {/* Environmental Impact */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-green-400">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Leaf className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-2xl">{EMOJIS.PLANT}</span>
            </div>
            <div className="text-4xl font-bold text-gray-900 mb-2">
              {Math.round(analytics.co2Prevented)} kg
            </div>
            <div className="text-sm font-semibold text-gray-700 mb-1">CO₂ Prevented</div>
            <div className="text-xs text-gray-600">{analytics.mealsPreserved} meals saved</div>
          </div>
        </div>

        {/* Summary Card */}
        <div className="bg-gradient-to-br from-primary-green to-emerald-600 rounded-2xl shadow-xl p-6 text-white">
          <h3 className="text-xl font-bold mb-4">Your Impact Summary</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-white/80 text-sm mb-1">Total Items Tracked</p>
              <p className="text-3xl font-bold">{analytics.totalItemsAdded}</p>
            </div>
            <div>
              <p className="text-white/80 text-sm mb-1">Waste Rate</p>
              <p className="text-3xl font-bold">{analytics.wasteRate.toFixed(1)}%</p>
            </div>
            <div>
              <p className="text-white/80 text-sm mb-1">Items Used</p>
              <p className="text-3xl font-bold text-green-200">{analytics.itemsUsed}</p>
            </div>
            <div>
              <p className="text-white/80 text-sm mb-1">Items Wasted</p>
              <p className="text-3xl font-bold text-red-200">{analytics.itemsWasted}</p>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-white/20">
            <p className="text-sm text-white/90 mb-3">
              {EMOJIS.LIGHTBULB} <strong>Want more insights?</strong>
            </p>
            <p className="text-sm text-white/80 mb-4">
              Upgrade to Premium for detailed charts, time-based analysis, category breakdowns, and more!
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="w-full bg-white text-primary-green py-3 rounded-xl font-semibold hover:bg-gray-100 transition-all"
            >
              Upgrade to Premium
            </button>
          </div>
        </div>

        {/* Tips Section */}
        {analytics.wasteRate > 30 && (
          <div className="mt-6 bg-yellow-50 rounded-2xl shadow-lg p-6 border-l-4 border-yellow-400">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              {EMOJIS.LIGHTBULB} Tips to Reduce Waste
            </h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-primary-green mt-0.5">{EMOJIS.CHECK}</span>
                <span>Check your fridge regularly to use items before they expire</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary-green mt-0.5">{EMOJIS.CHECK}</span>
                <span>Plan meals around items expiring soon</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary-green mt-0.5">{EMOJIS.CHECK}</span>
                <span>Freeze items if you won't use them in time</span>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
